import {inject, injectable} from 'inversify';

import type {ID} from '../../../../utils/utility-types';
import type {AppEventModel} from '../../../domain/AppEvent/AppEvent.model';
import type {FileModel} from '../../../domain/File/File.model';
import type {WsMessageModel} from '../../../domain/WsMessage/WsMessage.model';
import type {FileConvertWsService} from '../FileConvertWs.service';

import {
  emitter,
  WS_RECEIVE_MESSAGE_EVENT,
} from '../../../../utils/eventEmitter';
import {FileController} from '../../../domain/File/File.controller';
import {WsMessageMapperJrpc} from '../../../domain/WsMessage/mappers/WsMessage.mapper.jrpc';
import {WsMessageFactory} from '../../../domain/WsMessage/WsMessage.factory';

@injectable()
export class FileConvertWsServiceJrpc implements FileConvertWsService {
  private convertFiles: Map<ID, FileModel> = new Map<ID, FileModel>();
  private EVENT_FILE_CONVERTED: AppEventModel.SupportedSocketType =
    'file_converted';

  constructor(
    @inject(FileController) private readonly fileController: FileController,
    @inject(WsMessageFactory)
    private readonly wsMessageFactory: WsMessageFactory,
    @inject(WsMessageMapperJrpc)
    private readonly wsMessageMapper: WsMessageMapperJrpc,
  ) {
    this.handleEvent = this.handleEvent.bind(this);
    this.init();
  }

  private init() {
    emitter.on(WS_RECEIVE_MESSAGE_EVENT, this.handleEvent);
  }

  registerFileToConvert(file: FileModel): void {
    this.convertFiles.set(file.id, file);
  }

  private handleEvent(data: WsMessageMapperJrpc.ApiData) {
    if (data.event.type === this.EVENT_FILE_CONVERTED) {
      const modelProps = this.wsMessageMapper.mapDataToProps(data);
      const message = this.wsMessageFactory.create(modelProps);

      this.processFileConverted(message);
    }
  }

  private processFileConverted(message: WsMessageModel) {
    const fileId = message.file?.id;

    if (!fileId) {
      return;
    }

    const file = this.convertFiles.get(fileId);

    if (file) {
      this.fileController.handlerWsUpdate(file, message);
      this.convertFiles.delete(fileId);
    }
  }
}
